import React, { useState, useEffect, useRef } from "react";
import { capitalizeName } from "../utils/formatting";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  FlatList,
  Modal,
  Alert,
  ScrollView,
  ActivityIndicator,
} from "react-native";
import { useNavigate, useLocation } from "react-router-dom";
import { updateDocument, createDocument, getDocument } from "../db_calls";
import { getCollectionPath } from "../utils/dbPaths";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../firebase.config";
import { useBookings, useLocks, useAuthStore, getSelectedUserId } from "../stores";
import { createChangeTrackingEntry } from "../utils/helperFuns";
import { doc, updateDoc, arrayUnion, Timestamp } from "firebase/firestore";
import { db } from "../firebase.config";

const functions = getFunctions(app);
import {
  getLocksFromCache,
  getLockFromCache,
  getCombinationFromCache,
  addLockToCache,
} from "../services/locksCache";

// Helper function to format phone number
const formatPhoneNumber = (phone) => {
  if (!phone) return "";
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
      6
    )}`;
  }
  return phone;
};

const LockAssignment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const rental = location.state?.rental;

  // Zustand stores
  const bookingsFromStore = useBookings((state) => state.bookings);
  const locksFromStore = useLocks((state) => state.locks);

  const [assignedLocks, setAssignedLocks] = useState(
    rental?.assignedLocks || []
  );
  
  // Convert old format to new format when locks are loaded
  useEffect(() => {
    if (locksFromStore.length > 0 && assignedLocks.length > 0) {
      const needsConversion = assignedLocks.some(lock => typeof lock === 'number');
      if (needsConversion) {
        const converted = assignedLocks.map(lock => {
          if (typeof lock === 'number') {
            // Old format - convert to object
            const lockFromStore = locksFromStore.find(l => (l.tagNumber || parseInt(l.name)) === lock);
            return {
              tagNumber: lock,
              name: lockFromStore?.name || lock.toString(),
              combination: lockFromStore?.combo || lockFromStore?.combination || ""
            };
          }
          return lock; // Already an object
        });
        setAssignedLocks(converted);
        // Save converted format to database
        if (rental?.id) {
          const bookingsPath = getCollectionPath("bookings");
          updateDocument(bookingsPath, rental.id, {
            assignedLocks: converted,
          }).catch(err => console.error("Error updating lock format:", err));
        }
      }
    }
  }, [locksFromStore.length]);
  const [saving, setSaving] = useState(false);
  const [sendingLockCodes, setSendingLockCodes] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [showCombinationDialog, setShowCombinationDialog] = useState(false);
  const [selectedLockForCombination, setSelectedLockForCombination] =
    useState(null);
  const [combinationInput, setCombinationInput] = useState("");
  const [availableLocks, setAvailableLocks] = useState([]);
  const [showLockPickerModal, setShowLockPickerModal] = useState(false);
  const [selectedLocksForAssignment, setSelectedLocksForAssignment] = useState([]);
  const autoSendTimerRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  // Load assigned locks on component mount
  useEffect(() => {
    loadAssignedLocks();
  }, []);

  // Calculate available locks (not assigned to any booking)
  const calculateAvailableLocks = React.useCallback(() => {
    // Get all assigned lock tagNumbers from all bookings (excluding current booking)
    const allAssignedLockTags = new Set();
    bookingsFromStore.forEach((booking) => {
      // Skip the current booking - we want to show locks assigned to this booking as available for selection
      if (booking.id === rental?.id || booking.bookingId === rental?.id) {
        return;
      }
      if (booking.assignedLocks && Array.isArray(booking.assignedLocks)) {
        booking.assignedLocks.forEach((lock) => {
          // Handle both old format (number) and new format (object)
          const tagNumber = typeof lock === 'number' ? lock : (lock.tagNumber || parseInt(lock.name));
          allAssignedLockTags.add(tagNumber);
        });
      }
    });

    // Filter locks that are not assigned to any other booking
    const available = locksFromStore.filter((lock) => {
      const tagNumber = lock.tagNumber !== undefined ? lock.tagNumber : (lock.name ? parseInt(lock.name) : null);
      if (tagNumber === null) return false;
      return !allAssignedLockTags.has(tagNumber);
    });

    // Sort by name or tagNumber
    available.sort((a, b) => {
      const aName = a.name || a.tagNumber?.toString() || "";
      const bName = b.name || b.tagNumber?.toString() || "";
      return aName.localeCompare(bName);
    });

    setAvailableLocks(available);
    console.log("✅ Available locks calculated:", available.length);
  }, [bookingsFromStore, locksFromStore, assignedLocks]);

  // Calculate available locks on page load and when bookings/locks change
  useEffect(() => {
    calculateAvailableLocks();
  }, [calculateAvailableLocks]);

  // Auto-send timer: sends lock codes 5 minutes after first lock is added
  useEffect(() => {
    // Start timer when locks are assigned
    if (assignedLocks.length > 0 && !autoSendTimerRef.current) {
      console.log("⏰ Starting 5-minute auto-send timer for lock codes");
      autoSendTimerRef.current = setTimeout(() => {
        console.log("⏰ Timer expired - auto-sending lock codes");
        sendLockCodesAutomatic();
      }, 5 * 60 * 1000); // 5 minutes
    }

    // Cleanup timer on unmount
    return () => {
      if (autoSendTimerRef.current) {
        clearTimeout(autoSendTimerRef.current);
        autoSendTimerRef.current = null;
      }
    };
  }, [assignedLocks.length]);

  const loadAssignedLocks = async () => {
    if (!rental?.id) {
      console.log("No rental ID found, skipping assigned locks load");
      return;
    }

    try {
      console.log(
        "🔄 Loading assigned locks from database for rental:",
        rental.id
      );
      const bookingsPath = getCollectionPath("bookings");
      const rentalData = await getDocument(bookingsPath, rental.id);

      if (rentalData && rentalData.assignedLocks) {
        setAssignedLocks(rentalData.assignedLocks);
        console.log("✅ Loaded assigned locks:", rentalData.assignedLocks);
      } else {
        console.log("No assigned locks found in database");
        setAssignedLocks([]);
      }
    } catch (error) {
      console.error("❌ Error loading assigned locks:", error);
      // Don't show alert for this, just log the error
    }
  };

  const getLockCombination = (lockNumber) => {
    return getCombinationFromCache(lockNumber) || null;
  };

  const handleCombinationMissing = (lockNumber) => {
    setSelectedLockForCombination(lockNumber);
    setCombinationInput("");
    setShowCombinationDialog(true);
  };

  const handleCancelCombination = () => {
    setShowCombinationDialog(false);
    setCombinationInput("");
    setSelectedLockForCombination(null);
    setLockNumber(""); // Clear input field for next use
  };

  const sendLockCodesAutomatic = async () => {
    if (assignedLocks.length === 0) {
      return;
    }

    console.log("📤 Auto-sending lock codes (timer expired)");

    // Build the message with all lock codes
    const lockMessages = assignedLocks
      .sort((a, b) => {
        const aTag = typeof a === 'number' ? a : a.tagNumber;
        const bTag = typeof b === 'number' ? b : b.tagNumber;
        return aTag - bTag;
      })
      .map((lock) => {
        const tagNumber = typeof lock === 'number' ? lock : lock.tagNumber;
        const combination = typeof lock === 'number' ? getLockCombination(lock) : lock.combination;
        const name = typeof lock === 'number' ? tagNumber.toString() : lock.name;
        return `Lock ${name}: ${combination || "Unknown"}`;
      })
      .join("\n");

    const message = `Hi ${capitalizeName(rental.firstName || rental.fullName)}! Here are your lock combinations:\n\n${lockMessages}\n\nPlease keep these safe. Enjoy your rental! 🚴`;

    try {
      const sendSMS = httpsCallable(functions, "sendSMS");
      await sendSMS({
        to: rental.phone,
        message: message,
        customerName: rental.fullName,
        customerPhone: rental.phone,
        sentByUserId: getSelectedUserId(),
      });

      console.log("✅ Lock codes auto-sent via SMS");
    } catch (error) {
      console.error("❌ Failed to auto-send lock codes:", error);
    }
  };

  const handleSendLockCodes = async () => {
    if (assignedLocks.length === 0) {
      return;
    }

    // Build the message with all lock codes
    const lockMessages = assignedLocks
      .sort((a, b) => {
        const aTag = typeof a === 'number' ? a : a.tagNumber;
        const bTag = typeof b === 'number' ? b : b.tagNumber;
        return aTag - bTag;
      })
      .map((lock) => {
        const tagNumber = typeof lock === 'number' ? lock : lock.tagNumber;
        const combination = typeof lock === 'number' ? getLockCombination(lock) : lock.combination;
        const name = typeof lock === 'number' ? tagNumber.toString() : lock.name;
        return `Lock ${name}: ${combination || "Unknown"}`;
      })
      .join("\n");

    const message = `Hi ${capitalizeName(rental.firstName || rental.fullName)}! Here are your lock combinations:\n\n${lockMessages}\n\nPlease keep these safe. Enjoy your rental! 🚴`;

    const confirmSend = window.confirm(
      `Send lock codes to ${
        rental.firstName || rental.fullName
      }?\n\n${lockMessages}`
    );

    if (!confirmSend) {
      return;
    }

    setSendingLockCodes(true);

    try {
      // Cancel auto-send timer
      if (autoSendTimerRef.current) {
        clearTimeout(autoSendTimerRef.current);
        autoSendTimerRef.current = null;
        console.log("⏰ Auto-send timer canceled (user manually sent)");
      }

      const sendSMS = httpsCallable(functions, "sendSMS");
      await sendSMS({
        to: rental.phone,
        message: message,
        customerName: rental.fullName,
        customerPhone: rental.phone,
        sentByUserId: getSelectedUserId(),
      });

      console.log("✅ Lock codes sent via SMS");
      // No success alert - just close modal
    } catch (error) {
      console.error("❌ Failed to send lock codes:", error);
      alert("Failed to send lock codes: " + error.message);
    } finally {
      setSendingLockCodes(false);
    }
  };

  const ensureLockExistsInDatabase = async (lockNumber, combination = "") => {
    try {
      // Check if lock exists in database
      const locksPath = getCollectionPath("locks");
      const existingLock = await getDocument(locksPath, lockNumber.toString());

      if (!existingLock) {
        // Lock doesn't exist, create it
        console.log(
          "🔄 Creating new lock in database:",
          lockNumber,
          "with combination:",
          combination
        );
        const locksPath = getCollectionPath("locks");
        await createDocument(locksPath, {
          tagNumber: lockNumber,
          combination: combination,
          createdAt: new Date(),
          updatedAt: new Date(),
        });
        console.log("✅ Lock created in database:", lockNumber);
      } else {
        console.log("✅ Lock already exists in database:", lockNumber);
        // If lock exists but has no combination, update it
        if (!existingLock.combination && combination) {
          console.log("🔄 Updating lock combination in database:", lockNumber);
          const locksPath = getCollectionPath("locks");
          await updateDocument(locksPath, lockNumber.toString(), {
            combination: combination,
          });
          console.log("✅ Lock combination updated in database");
        }
      }
    } catch (error) {
      console.error("❌ Error ensuring lock exists:", error);
      throw error;
    }
  };

  const saveCombination = async () => {
    if (!combinationInput.trim()) {
      alert("Error: " + "Please enter a combination");
      return;
    }

    if (!rental?.id) {
      alert("Error: " + "No rental ID found");
      return;
    }

    // Save combination to cache (will be synced to database via ensureLockExistsInDatabase)
    console.log(
      `💾 Saving combination for lock #${selectedLockForCombination}`
    );
    addLockToCache({
      tagNumber: selectedLockForCombination,
      combination: combinationInput.trim(),
      updatedAt: new Date().toISOString(),
    });

    // Check if already assigned
    const isAlreadyAssigned = assignedLocks.some(
      (assigned) => (typeof assigned === 'number' ? assigned : assigned.tagNumber) === selectedLockForCombination
    );
    
    if (!isAlreadyAssigned) {
      // Find lock from store to get name
      const lockFromStore = locksFromStore.find(
        (l) => (l.tagNumber || parseInt(l.name)) === selectedLockForCombination
      );
      const lockName = lockFromStore?.name || selectedLockForCombination.toString();
      
      // Create lock object with name, tagNumber, and combination
      const lockObject = {
        tagNumber: selectedLockForCombination,
        name: lockName,
        combination: combinationInput.trim()
      };
      
      const newAssignedLocks = [...assignedLocks, lockObject];
      setAssignedLocks(newAssignedLocks);
      console.log("✅ Lock added:", lockObject);

      // Ensure lock exists in database with combination
      try {
        await ensureLockExistsInDatabase(
          selectedLockForCombination,
          combinationInput.trim()
        );
      } catch (error) {
        console.error("❌ Error ensuring lock exists:", error);
        alert("Error: Failed to create lock in database: " + error.message);
        return;
      }

      // Auto-save to database immediately
      try {
        console.log("🔄 Auto-saving to database:", {
          rentalId: rental.id,
          assignedLocks: newAssignedLocks,
        });
        const bookingsPath = getCollectionPath("bookings");
        const bookingRef = doc(db, bookingsPath, rental.id);

        // Get user metadata for change tracking (if authenticated)
        const selectedUser = useAuthStore.getState().selectedUser;
        const userMetadata = selectedUser ? {
          userId: selectedUser.id,
          firstName: selectedUser.firstName || "",
          lastName: selectedUser.lastName || "",
        } : null;

        const updateData = {
          assignedLocks: newAssignedLocks,
          updatedAt: Timestamp.now(),
        };

        // Add change tracking if user metadata is available
        if (userMetadata) {
          const changeEntry = createChangeTrackingEntry(userMetadata, {
            changeType: "lock_assignment",
            fieldChanged: "assignedLocks",
            description: "Lock assignment updated",
            timestamp: Timestamp.now(),
          });

          if (changeEntry) {
            updateData.editedBy = arrayUnion(changeEntry);
          }
        }

        await updateDoc(bookingRef, updateData);
        console.log("✅ Lock auto-saved to database");
        
        // Recalculate availability
        calculateAvailableLocks();
      } catch (error) {
        console.error("❌ Error saving lock to database:", error);
        alert("Error: Failed to save lock to database: " + error.message);
        return;
      }
    }

    setShowCombinationDialog(false);
    setCombinationInput("");
    setSelectedLockForCombination(null);
  };

  // Analyze image for lock number using OCR
  const analyzeImageForLockNumber = async (imageData) => {
    try {
      setAnalyzing(true);

      // Simulate OCR analysis - in a real implementation, you'd use a service like:
      // - Google Vision API
      // - AWS Textract
      // - Azure Computer Vision
      // - Tesseract.js for client-side OCR

      // For now, we'll simulate the analysis with a random number
      // In production, replace this with actual OCR service call
      await new Promise((resolve) => setTimeout(resolve, 2000)); // Simulate processing time

      // Simulate OCR result - extract numbers from image
      const mockOCRResult = Math.floor(Math.random() * 999) + 1; // Random number 1-999

      console.log("🔍 OCR Analysis Result:", mockOCRResult);

      if (mockOCRResult && mockOCRResult > 0) {
        // Find the lock in all locks
        const foundLock = locksFromStore.find(
          (lock) => {
            const tagNumber = lock.tagNumber || parseInt(lock.name);
            return tagNumber === mockOCRResult;
          }
        );
        
        if (foundLock) {
          setShowCamera(false);
          // Use handleSelectLock to add the lock
          await handleSelectLock(foundLock);
          alert(`Success! Found lock: ${foundLock.name || mockOCRResult}`);
        } else {
          setShowCamera(false);
          alert(`Lock number ${mockOCRResult} found but not in locks database. Please add it first.`);
        }
      } else {
        throw new Error("No number found in image");
      }
    } catch (error) {
      console.error("❌ OCR Analysis failed:", error);
      alert("Image Recognition Failed: " +
        "Could not read the lock number from the image. Please try again or enter the number manually.",
        [
          { text: "Try Again", onPress: () => setShowCamera(true) },
          { text: "Enter Manually", onPress: () => setShowCamera(false) },
        ]
      );
    } finally {
      setAnalyzing(false);
    }
  };

  const startCamera = async () => {
    try {
      console.log("📷 Starting camera...");
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" }, // Use back camera on mobile
        audio: false,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }

      console.log("✅ Camera started successfully");
    } catch (error) {
      console.error("❌ Camera access failed:", error);
      alert("Camera Error: " +
        "Could not access camera. Please ensure camera permissions are granted.",
        [{ text: "OK", onPress: () => setShowCamera(false) }]
      );
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      console.log("📷 Camera stopped");
    }
  };

  // Start camera when modal opens
  useEffect(() => {
    if (showCamera) {
      startCamera();
    } else {
      stopCamera();
    }

    // Cleanup on unmount
    return () => {
      stopCamera();
    };
  }, [showCamera]);

  const handleCameraCapture = async () => {
    try {
      if (!videoRef.current) {
        throw new Error("Video element not available");
      }

      console.log("📸 Capturing image from camera...");

      // Create a canvas to capture the current video frame
      const canvas = document.createElement("canvas");
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);

      // Convert canvas to blob
      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", 0.8)
      );

      console.log("✅ Image captured, size:", blob.size, "bytes");

      await analyzeImageForLockNumber(blob);
    } catch (error) {
      console.error("❌ Camera capture failed:", error);
      window.alert("Camera Error", "Failed to capture image. Please try again.");
    }
  };

  // Handle lock selection from picker
  const handleSelectLock = async (lock) => {
    if (!rental?.id) {
      alert("Error: No rental ID found");
      return;
    }

    const tagNumber = lock.tagNumber !== undefined ? lock.tagNumber : (lock.name ? parseInt(lock.name) : null);
    if (!tagNumber) {
      alert("Error: Invalid lock tag number");
      return;
    }

    // Check if already assigned
    const isAlreadyAssigned = assignedLocks.some(
      (assigned) => (typeof assigned === 'number' ? assigned : assigned.tagNumber) === tagNumber
    );
    if (isAlreadyAssigned) {
      return; // Already assigned, skip silently
    }

    // Get combination and name from lock object
    const combination = lock.combo || lock.combination || "";
    const lockName = lock.name || lock.tagNumber?.toString() || tagNumber.toString();
    
    if (!combination) {
      alert("Error: Lock does not have a combination in the database");
      return;
    }

    // Create lock object with name, tagNumber, and combination
    const lockObject = {
      tagNumber: tagNumber,
      name: lockName,
      combination: combination
    };

    // Add to list and save to database
    const newAssignedLocks = [...assignedLocks, lockObject];
    setAssignedLocks(newAssignedLocks);
    console.log("✅ Lock added:", lockObject);

    // Auto-save to database
    try {
      console.log("🔄 Auto-saving to database:", {
        rentalId: rental.id,
        assignedLocks: newAssignedLocks,
      });
      const bookingsPath = getCollectionPath("bookings");
      await updateDocument(bookingsPath, rental.id, {
        assignedLocks: newAssignedLocks,
      });
      console.log("✅ Lock auto-saved to database");
      
      // Recalculate availability
      calculateAvailableLocks();
    } catch (error) {
      console.error("❌ Error saving lock to database:", error);
      alert("Error: Failed to save lock to database: " + error.message);
      // Revert state
      setAssignedLocks(assignedLocks);
    }
  };

  const handleRemoveLock = async (lockToRemove) => {
    try {
      // Handle both old format (number) and new format (object)
      const tagNumberToRemove = typeof lockToRemove === 'number' ? lockToRemove : lockToRemove.tagNumber;
      const newAssignedLocks = assignedLocks.filter((lock) => {
        const tagNumber = typeof lock === 'number' ? lock : lock.tagNumber;
        return tagNumber !== tagNumberToRemove;
      });
      setAssignedLocks(newAssignedLocks);

      // Update the database
      if (rental?.id) {
        console.log("🔄 Removing lock from database:", tagNumberToRemove);
        const bookingsPath = getCollectionPath("bookings");
        await updateDocument(bookingsPath, rental.id, {
          assignedLocks: newAssignedLocks,
        });
        console.log("✅ Lock removed from database successfully");
        
        // Recalculate availability to include the removed lock
        calculateAvailableLocks();
      }
    } catch (error) {
      console.error("❌ Error removing lock from database:", error);
      // Revert the state change if database update fails
      setAssignedLocks(assignedLocks);
      alert("Failed to remove lock from database. Please try again.");
    }
  };

  const handleFinish = async () => {
    if (!rental?.id) {
      alert("No rental information found");
      return;
    }

    setSaving(true);

    try {
      // Save assigned locks to booking document
      const bookingsPath = getCollectionPath("bookings");
      await updateDocument(bookingsPath, rental.id, {
        assignedLocks: assignedLocks,
        updatedAt: new Date(),
      });

      console.log("✅ Locks saved to booking:", assignedLocks);

      // Navigate back
      navigate(-1);
    } catch (error) {
      console.error("❌ Error saving locks:", error);
      alert("Failed to save locks: " + error.message);
      setSaving(false);
    }
  };

  if (!rental) {
    return (
      <View style={styles.container}>
        <Text style={styles.errorText}>No rental selected</Text>
        <TouchableOpacity dataSet={{ hover: "lift" }}
          style={styles.backButton}
          onPress={() => navigate(-1)}
        >
          <Text style={styles.backButtonText}>← Back</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <View style={styles.container}>
      {/* Floating Back Button */}
      <TouchableOpacity dataSet={{ hover: "lift" }} 
        style={[
          styles.floatingBackButton,
          isDesktop && styles.floatingBackButtonDesktop
        ]} 
        onPress={() => navigate(-1)}
      >
        <Text style={styles.floatingBackButtonText}>←</Text>
      </TouchableOpacity>

      <View style={isDesktop ? styles.desktopWrapper : null}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
          {/* White space at top */}
          <View style={styles.topSpacer} />

          {/* Customer Name Header */}
          <View style={styles.customerNameSection}>
            <Text style={styles.customerNameText}>
              {rental.fullName || rental.firstName || "Customer"} - Lock Assignments
            </Text>
          </View>

          {/* Lock Picker Section */}
          <View style={styles.pickerSection}>
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={styles.pickerButton}
              onPress={() => {
                setSelectedLocksForAssignment([]);
                setShowLockPickerModal(true);
              }}
            >
              <Text style={styles.pickerButtonText}>Select Locks</Text>
            </TouchableOpacity>
          </View>

          {/* Assigned Locks List */}
          {assignedLocks.length > 0 && (
            <View style={styles.assignedLocksSection}>
              <Text style={styles.sectionTitle}>
                Assigned Locks ({assignedLocks.length})
              </Text>
              {assignedLocks.sort((a, b) => {
                const aTag = typeof a === 'number' ? a : a.tagNumber;
                const bTag = typeof b === 'number' ? b : b.tagNumber;
                return aTag - bTag;
              }).map((lock, index) => {
                // Handle both old format (number) and new format (object)
                const tagNumber = typeof lock === 'number' ? lock : lock.tagNumber;
                const lockName = typeof lock === 'number' 
                  ? (locksFromStore.find(l => (l.tagNumber || parseInt(l.name)) === lock)?.name || `#${lock}`)
                  : lock.name;
                const combination = typeof lock === 'number' 
                  ? getLockCombination(lock) 
                  : lock.combination;
                
                return (
                  <View key={`${tagNumber}-${index}`} style={styles.lockTagItem}>
                    <View style={styles.lockTagInfo}>
                      <Text style={styles.lockTagName}>🔒 {lockName}</Text>
                      <Text style={styles.lockCombination}>
                        🔢 {combination || "No combination"}
                      </Text>
                    </View>
                    <TouchableOpacity dataSet={{ hover: "lift" }}
                      style={styles.removeLockButton}
                      onPress={() => handleRemoveLock(lock)}
                    >
                      <Text style={styles.removeLockButtonText}>🗑️</Text>
                    </TouchableOpacity>
                  </View>
                );
              })}
            </View>
          )}

          {/* Upload Button - Keep this */}
          <View style={styles.uploadSection}>
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={[
                styles.sendCodesButton,
                assignedLocks.length === 0 && styles.sendCodesButtonDisabled,
              ]}
              onPress={handleSendLockCodes}
              disabled={assignedLocks.length === 0}
            >
              <Text style={styles.sendCodesButtonText}>📤 Send Lock Codes</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>

      {/* Camera Modal */}
      <Modal
        visible={showCamera}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowCamera(false)}
      >
        <View style={styles.cameraModal}>
          <View style={styles.cameraContainer}>
            <Text style={styles.cameraTitle}>Scan Lock Tag</Text>
            <Text style={styles.cameraSubtitle}>
              Position the lock tag number in the frame and tap capture
            </Text>

            {/* Camera Preview Area */}
            <View style={styles.cameraPreview}>
              <video
                ref={videoRef}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: 8,
                }}
                autoPlay
                playsInline
                muted
              />
            </View>

            {/* Camera Controls */}
            <View style={styles.cameraControls}>
              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={styles.cancelButton}
                onPress={() => setShowCamera(false)}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>

              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={[
                  styles.captureButton,
                  analyzing && styles.captureButtonDisabled,
                ]}
                onPress={handleCameraCapture}
                disabled={analyzing}
              >
                <Text style={styles.captureButtonText}>
                  {analyzing ? "Analyzing..." : "📸 Capture"}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Sending Lock Codes Blocking Modal */}
      <Modal
        visible={sendingLockCodes}
        transparent={true}
        animationType="fade"
        onRequestClose={() => {}} // Prevent closing while sending
      >
        <View style={styles.blockingModalOverlay}>
          <View style={styles.blockingModalContainer}>
            <ActivityIndicator size="large" color="#0E7C6B" />
            <Text style={styles.blockingModalText}>Sending lock codes...</Text>
          </View>
        </View>
      </Modal>

      {/* Lock Picker Modal */}
      <Modal
        visible={showLockPickerModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowLockPickerModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <Text style={styles.modalTitle}>Select Locks</Text>
            
            {/* Save Button at Top */}
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={[
                styles.saveLocksButton,
                selectedLocksForAssignment.length === 0 && styles.saveLocksButtonDisabled,
              ]}
              onPress={async () => {
                if (selectedLocksForAssignment.length === 0) return;
                
                // Process each selected lock
                for (const lock of selectedLocksForAssignment) {
                  await handleSelectLock(lock);
                }
                
                setSelectedLocksForAssignment([]);
                setShowLockPickerModal(false);
              }}
              disabled={selectedLocksForAssignment.length === 0}
            >
              <Text style={styles.saveLocksButtonText}>
                Save Selected ({selectedLocksForAssignment.length})
              </Text>
            </TouchableOpacity>

            {/* Locks List */}
            {availableLocks.length === 0 ? (
              <View style={styles.emptyLocksContainer}>
                <Text style={styles.emptyLocksText}>No available locks</Text>
              </View>
            ) : (
              <FlatList
                data={availableLocks}
                keyExtractor={(item) => item.id || item.tagNumber?.toString() || item.name}
                renderItem={({ item }) => {
                  const lockName = item.name || item.tagNumber?.toString() || "Unknown";
                  const tagNumber = item.tagNumber !== undefined ? item.tagNumber : (item.name ? parseInt(item.name) : null);
                  const isSelected = selectedLocksForAssignment.some(
                    (selected) => (selected.tagNumber !== undefined ? selected.tagNumber : parseInt(selected.name)) === tagNumber
                  );
                  
                  return (
                    <TouchableOpacity
                      style={styles.lockPickerItem}
                      onPress={() => {
                        if (isSelected) {
                          setSelectedLocksForAssignment(
                            selectedLocksForAssignment.filter(
                              (selected) => (selected.tagNumber !== undefined ? selected.tagNumber : parseInt(selected.name)) !== tagNumber
                            )
                          );
                        } else {
                          setSelectedLocksForAssignment([...selectedLocksForAssignment, item]);
                        }
                      }}
                    >
                      <View style={[
                        styles.checkbox,
                        isSelected && styles.checkboxChecked
                      ]}>
                        {isSelected && (
                          <Text style={styles.checkboxCheckmark}>✓</Text>
                        )}
                      </View>
                      <Text style={styles.lockPickerItemText}>{lockName}</Text>
                    </TouchableOpacity>
                  );
                }}
                style={styles.lockPickerList}
              />
            )}

            {/* Cancel Button */}
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={styles.cancelModalButton}
              onPress={() => {
                setSelectedLocksForAssignment([]);
                setShowLockPickerModal(false);
              }}
            >
              <Text style={styles.cancelModalButtonText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Combination Dialog */}
      <Modal
        visible={showCombinationDialog}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowCombinationDialog(false)}
      >
        <View style={styles.dialogModal}>
          <View style={styles.dialogContainer}>
            <Text style={styles.dialogTitle}>Add Combination</Text>
            <Text style={styles.dialogSubtitle}>
              Enter combination for lock #{selectedLockForCombination}
            </Text>

            <TextInput
              style={styles.combinationInput}
              placeholder="Enter combination"
              placeholderTextColor="#8A9794"
              value={combinationInput}
              onChangeText={setCombinationInput}
              autoFocus={false}
            />

            <View style={styles.dialogButtons}>
              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={styles.cancelDialogButton}
                onPress={handleCancelCombination}
              >
                <Text style={styles.cancelDialogButtonText}>Cancel</Text>
              </TouchableOpacity>

              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={styles.saveDialogButton}
                onPress={saveCombination}
              >
                <Text style={styles.saveDialogButtonText}>Save</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  desktopWrapper: {
    width: "100%",
    maxWidth: 800,
    flex: 1,
    alignSelf: "center",
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 20,
  },
  topSpacer: {
    height: 20,
  },
  customerNameSection: {
    marginHorizontal: 16,
    marginBottom: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#DDE3E2",
  },
  customerNameText: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#2E3A38",
  },
  floatingBackButton: {
    position: 'absolute',
    top: 80,
    left: 16,
    width: 44,
    height: 44,
    backgroundColor: 'rgba(0, 122, 255, 0.5)',
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 5,
  },
  floatingBackButtonDesktop: {
    left: 'calc(50% - 400px + 16px)',
  },
  floatingBackButtonText: {
    color: '#FFFFFF',
    fontSize: 24,
    fontWeight: 'bold',
  },
  pickerSection: {
    marginHorizontal: 16,
    marginBottom: 20,
  },
  pickerLabel: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 8,
  },
  pickerButton: {
    backgroundColor: "#F4F6F6",
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignItems: "center",
  },
  pickerButtonText: {
    fontSize: 14,
    color: "#0E7C6B",
    fontWeight: "600",
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    padding: 20,
    width: "90%",
    maxWidth: 500,
    maxHeight: "80%",
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 16,
    textAlign: "center",
  },
  saveLocksButton: {
    backgroundColor: "#1F8A5B",
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 6,
    alignItems: "center",
    marginBottom: 16,
  },
  saveLocksButtonDisabled: {
    backgroundColor: "#DDE3E2",
    opacity: 0.5,
  },
  saveLocksButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  lockPickerList: {
    maxHeight: 400,
    marginBottom: 16,
  },
  lockPickerItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#EDF1F0",
  },
  checkbox: {
    width: 24,
    height: 24,
    borderWidth: 2,
    borderColor: "#0E7C6B",
    borderRadius: 4,
    marginRight: 12,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FFFFFF",
  },
  checkboxChecked: {
    backgroundColor: "#0E7C6B",
  },
  checkboxCheckmark: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "bold",
  },
  lockPickerItemText: {
    fontSize: 16,
    color: "#2E3A38",
    flex: 1,
  },
  emptyLocksContainer: {
    paddingVertical: 40,
    alignItems: "center",
  },
  emptyLocksText: {
    fontSize: 16,
    color: "#8A9794",
  },
  cancelModalButton: {
    backgroundColor: "#EDF1F0",
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 6,
    alignItems: "center",
  },
  cancelModalButtonText: {
    color: "#2E3A38",
    fontSize: 16,
    fontWeight: "600",
  },
  assignedLocksSection: {
    marginHorizontal: 16,
    marginBottom: 20,
  },
  lockTagName: {
    fontSize: 16,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 4,
  },
  inputSection: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: "#DDE3E2",
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 12,
  },
  inputRow: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: 12,
  },
  input: {
    width: 100,
    height: 45,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    paddingHorizontal: 12,
    fontSize: 16,
    backgroundColor: "#F4F6F6",
    outlineStyle: "none",
    marginLeft: 20,
  },
  enterButton: {
    backgroundColor: "#1F8A5B",
    width: 45,
    height: 45,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
    marginLeft: 20,
  },
  enterButtonText: {
    color: "#FFFFFF",
    fontSize: 20,
    fontWeight: "bold",
  },
  locksListSection: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    marginHorizontal: 16,
    padding: 16,
    borderRadius: 8,
    marginBottom: 16,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  locksList: {
    paddingTop: 8,
  },
  lockTagItem: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 12,
    backgroundColor: "#F4F6F6",
    borderRadius: 6,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: "#DDE3E2",
  },
  lockTagInfo: {
    flex: 1,
  },
  lockTagNumber: {
    fontSize: 16,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 4,
  },
  lockCombination: {
    fontSize: 14,
    color: "#1F8A5B",
    fontWeight: "500",
  },
  addCombinationButton: {
    backgroundColor: "#0E7C6B",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 5,
    alignSelf: "flex-start",
  },
  addCombinationText: {
    color: "#FFFFFF",
    fontSize: 12,
    fontWeight: "600",
  },
  removeLockButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: "#C4453D",
    alignItems: "center",
    justifyContent: "center",
  },
  removeLockButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "bold",
  },
  finishedButtonRow: {
    marginTop: 16,
  },
  finishedButtonFull: {
    backgroundColor: "#1F8A5B",
    paddingVertical: 8,
    borderRadius: 6,
    alignItems: "center",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 4,
  },
  finishedButtonDisabled: {
    backgroundColor: "#AEB8B6",
  },
  finishedButtonText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "bold",
  },
  bottomNav: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#FFFFFF",
    paddingVertical: 15,
    paddingHorizontal: 16,
    borderTopWidth: 1,
    borderTopColor: "#DDE3E2",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  backButton: {
    paddingVertical: 5,
    paddingHorizontal: 10,
  },
  backButtonText: {
    fontSize: 24,
    color: "#0E7C6B",
    fontWeight: "bold",
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#2E3A38",
  },
  headerSpacer: {
    width: 44,
  },
  errorText: {
    fontSize: 18,
    color: "#C4453D",
    textAlign: "center",
    marginTop: 40,
  },
  cameraButton: {
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 8,
    marginLeft: 20,
  },
  cameraButtonText: {
    fontSize: 30,
  },
  uploadSection: {
    marginHorizontal: 16,
    marginBottom: 20,
  },
  sendCodesButton: {
    backgroundColor: "#0E7C6B",
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  },
  sendCodesButtonDisabled: {
    opacity: 0.3,
    backgroundColor: "#DDE3E2",
  },
  sendCodesButtonText: {
    fontSize: 16,
    color: "#FFFFFF",
    fontWeight: "600",
  },
  cameraModal: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.8)",
    justifyContent: "center",
    alignItems: "center",
  },
  cameraContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    padding: 24,
    margin: 20,
    width: "90%",
    maxWidth: 400,
  },
  cameraTitle: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#2E3A38",
    textAlign: "center",
    marginBottom: 8,
  },
  cameraSubtitle: {
    fontSize: 16,
    color: "#5C6B68",
    textAlign: "center",
    marginBottom: 24,
  },
  cameraPreview: {
    height: 200,
    backgroundColor: "#EDF1F0",
    borderRadius: 8,
    borderWidth: 2,
    borderColor: "#DDE3E2",
    borderStyle: "dashed",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 24,
  },
  cameraPreviewText: {
    fontSize: 18,
    color: "#5C6B68",
    marginBottom: 8,
  },
  cameraPreviewSubtext: {
    fontSize: 14,
    color: "#8A9794",
    textAlign: "center",
  },
  cameraControls: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 16,
  },
  cancelButton: {
    flex: 1,
    backgroundColor: "#EDF1F0",
    paddingVertical: 12,
    borderRadius: 6,
    alignItems: "center",
  },
  cancelButtonText: {
    fontSize: 16,
    color: "#2E3A38",
    fontWeight: "600",
  },
  captureButton: {
    flex: 1,
    backgroundColor: "#0E7C6B",
    paddingVertical: 12,
    borderRadius: 6,
    alignItems: "center",
  },
  captureButtonDisabled: {
    backgroundColor: "#AEB8B6",
  },
  captureButtonText: {
    fontSize: 16,
    color: "#FFFFFF",
    fontWeight: "600",
  },
  dialogModal: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.8)",
    justifyContent: "center",
    alignItems: "center",
  },
  dialogContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    padding: 24,
    margin: 20,
    width: "90%",
    maxWidth: 400,
  },
  dialogTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#2E3A38",
    textAlign: "center",
    marginBottom: 8,
  },
  dialogSubtitle: {
    fontSize: 16,
    color: "#5C6B68",
    textAlign: "center",
    marginBottom: 20,
  },
  combinationInput: {
    height: 45,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    paddingHorizontal: 12,
    fontSize: 16,
    backgroundColor: "#F4F6F6",
    marginBottom: 20,
  },
  dialogButtons: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 12,
  },
  cancelDialogButton: {
    flex: 1,
    backgroundColor: "#EDF1F0",
    paddingVertical: 12,
    borderRadius: 6,
    alignItems: "center",
  },
  cancelDialogButtonText: {
    fontSize: 16,
    color: "#2E3A38",
    fontWeight: "600",
  },
  saveDialogButton: {
    flex: 1,
    backgroundColor: "#1F8A5B",
    paddingVertical: 12,
    borderRadius: 6,
    alignItems: "center",
  },
  saveDialogButtonText: {
    fontSize: 16,
    color: "#FFFFFF",
    fontWeight: "600",
  },
  blockingModalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    justifyContent: "center",
    alignItems: "center",
  },
  blockingModalContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    padding: 24,
    alignItems: "center",
    minWidth: 200,
  },
  blockingModalText: {
    marginTop: 16,
    fontSize: 16,
    color: "#2E3A38",
    fontWeight: "500",
  },
});

export default LockAssignment;
